import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../context/ToastContext';
import Loading from '../components/Loading';

export default function ResetPassword() {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useToast();

    const queryParams = new URLSearchParams(window.location.search);
    const email = location.state?.email || queryParams.get('email');
    const otp = location.state?.otp || queryParams.get('otp');
    const type = location.state?.type || queryParams.get('type') || 'forgot_password';

    useEffect(() => {
        if (!email || !otp) {
            navigate(type === 'password_update' ? '/profile' : '/forgot-password');
        }
    }, [email, otp, navigate, type]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            return toast.error('Passwords do not match');
        }
        if (newPassword.length < 8) {
            return toast.error('Password must be at least 8 characters');
        }
        if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
            return toast.error('Password must include uppercase, lowercase, number, and special character');
        }

        setLoading(true);
        try {
            await api.post('/api/auth/reset-password', { email, otp, newPassword, type });
            toast.success('Password update successful!');
            navigate(type === 'password_update' ? '/profile' : '/login');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-extrabold text-gray-900">Reset Password</h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Create a strong new password for your account.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                            <input
                                type="password"
                                required
                                placeholder="••••••••"
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                            />
                        </div>

                        {/* Password Requirement Checklist */}
                        <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-2">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Security Requirements</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                <div className={`text-[11px] flex items-center ${newPassword.length >= 8 ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-1 h-1 rounded-full mr-1.5 ${newPassword.length >= 8 ? 'bg-green-600' : 'bg-gray-300'}`} />
                                    8+ characters
                                </div>
                                <div className={`text-[11px] flex items-center ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-1 h-1 rounded-full mr-1.5 ${/[A-Z]/.test(newPassword) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                    Uppercase
                                </div>
                                <div className={`text-[11px] flex items-center ${/[a-z]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-1 h-1 rounded-full mr-1.5 ${/[a-z]/.test(newPassword) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                    Lowercase
                                </div>
                                <div className={`text-[11px] flex items-center ${/[0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-1 h-1 rounded-full mr-1.5 ${/[0-9]/.test(newPassword) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                    Number
                                </div>
                                <div className={`text-[11px] flex items-center ${/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-1 h-1 rounded-full mr-1.5 ${/[^A-Za-z0-9]/.test(newPassword) ? 'bg-green-600' : 'bg-gray-300'}`} />
                                    Special Char
                                </div>
                            </div>
                            
                            {/* Strength Bar */}
                            <div className="mt-3 h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full transition-all duration-300 ${
                                        [
                                            newPassword.length >= 8,
                                            /[A-Z]/.test(newPassword),
                                            /[a-z]/.test(newPassword),
                                            /[0-9]/.test(newPassword),
                                            /[^A-Za-z0-9]/.test(newPassword)
                                        ].filter(Boolean).length <= 2 ? 'bg-red-500 w-1/3' : 
                                        [
                                            newPassword.length >= 8,
                                            /[A-Z]/.test(newPassword),
                                            /[a-z]/.test(newPassword),
                                            /[0-9]/.test(newPassword),
                                            /[^A-Za-z0-9]/.test(newPassword)
                                        ].filter(Boolean).length <= 4 ? 'bg-yellow-500 w-2/3' : 'bg-green-500 w-full'
                                    }`}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
                        <input
                            type="password"
                            required
                            placeholder="••••••••"
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-lg shadow-lg shadow-primary-200 transition-all disabled:opacity-50 flex items-center justify-center"
                    >
                        {loading ? <Loading mode="button" message="Updating..." /> : 'Update Password'}
                    </button>
                </form>
            </div>
        </div>
    );
}
